<template>
  <div
    v-if="showFiltersBlock"
    class="filters-block"
    :class="{ 'hstack gap-2': $slots.filters }"
  >
    <div class="input-group" v-if="!date && pagination">
      <input
        class="form-control"
        :placeholder="t('common.searchPlaceholder')"
        v-model="searchTerm"
      />
      <button
        class="btn btn-outline-secondary text-nowrap"
        type="button"
        :disabled="!hasFilters"
        @click="resetFilters"
      >
        {{ t('common.resetFilters') }}
      </button>
    </div>
    <DateSelect
      v-if="date"
      :report-type="reportType"
      @create_report="handleCreateReport"
    />
    <slot name="filters" />
  </div>
  <slot
    :sort="sortFn"
    v-if="total || isfilter || hasFilters"
  />
  <div
    v-if="(total && pagination) || $slots.pagerExtra"
    class="d-flex flex-wrap align-items-center gap-3 mt-3"
    :class="total && pagination ? 'justify-content-between' : 'justify-content-end'"
  >
    <Pagination
      v-if="total && pagination"
      :total-item="total"
      :current-page="Number(model.page) || 1"
      :on-page="Number(model.onPage) || 20"
      :on-page-arr="pageSizeOptions"
      @page-change="modelValueSet"
    />
    <slot name="pagerExtra" />
  </div>

  <!-- Спиннер при загрузке -->
  <div
    v-if="isLoading"
    class="text-center py-4"
  >
    <i class="bi bi-arrow-clockwise spinning"></i>
    <div class="mt-2">{{ t('common.loading') }}</div>
  </div>

  <!-- Пусто после загрузки -->
  <slot
    name="empty"
    v-else-if="isEmpty"
  >
    {{ t('common.noRecords') }}
  </slot>
</template>
<script setup>
import { watchDebounced } from '@vueuse/core'
import { computed, inject, onMounted, provide, ref, shallowRef, toValue, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteLeave } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useListStore } from '@/stores/list'
import Pagination from '@/components/pagination/pagination.vue'
import DateSelect from '@/components/dateselect.vue'

const { t } = useI18n()
const emit = defineEmits(['create_report'])
const props = defineProps({
  total: {
    type: Number,
    default: undefined,
  },
  date: {
    type: Boolean,
    default: false,
  },
  reportType: {
    type: String,
    default: '',
  },
  pagination: {
    type: Boolean,
    default: true,
  },
  persist: {
    type: Boolean,
    default: true,
  },
  configurable: Boolean,
  columns: Array,
  loading: {
    type: Boolean,
    default: false,
  },
  pageSizeOptions: {
    type: Array,
    default: undefined,
  },
  /** На главной задаёт отдельный ключ хранилища для этого списка (например 'orders_my'), чтобы пагинация не общая для всех блоков. */
  persistKey: {
    type: String,
    default: '',
  },
  /** Скрыть блок фильтров (кнопка настройки вынесена в шапку родителя) */
  hideFiltersBlock: {
    type: Boolean,
    default: false,
  },
})
const model = defineModel({
  type: Object,
  default: () => ({
    page: 1,
    onPage: 20,
    search: '',
    order_direction: 'ASC',
    order_field: '',
    filter: '',
    filter_id: '',
  }),
})
const isHomePage = ref(false)
onMounted(() => {
  isHomePage.value = document.location.pathname === '/'
})

const searchTerm = shallowRef('')
const listStore = props.persist ? useListStore() : null
const { historyItem } = props.persist && listStore ? storeToRefs(listStore) : { historyItem: null }
const modalstack = props.persist ? inject('modalstack', () => false, true) : null
const isfilter = ref(false)

// Вычисляемые свойства для состояний списка
const isLoading = computed(() => props.loading || props.total === null || props.total === undefined)
const isEmpty = computed(() => props.total === 0)

const showDateWhenEmpty = computed(() => props.date && props.reportType === 'acts_pp')
const showFiltersBlock = computed(() => {
  if (props.hideFiltersBlock) {
    return false
  }
  if (props.total === null || props.total === undefined) {
    return false
  }
  if (showDateWhenEmpty) {
    return true
  }
  if (props.date) {
    return true
  }
  return props.pagination
})

watchDebounced(
  searchTerm,
  (search, osearch) => {
    // workaround: check if search term not changed
    if (search === undefined && osearch === '') {
      return
    }
    modelValueSet({ page: 1, search })
  },
  { debounce: 500 }
)

const hasFilters = computed(() => {
  const s = searchTerm.value && String(searchTerm.value).trim()
  return Boolean(
    model.value.filter?.length ||
      model.value.filter_id?.length ||
      s
  )
})

function resetFilters() {
  isfilter.value = false
  searchTerm.value = ''
  const next = {
    ...model.value,
    page: 1,
    search: '',
    filter: '',
    filter_id: '',
  }
  delete next.order_field
  delete next.order_direction
  model.value = next
}

if (listStore && !toValue(modalstack)) {
  onBeforeRouteLeave((to, from) => {
    if (routeNamespace(from) !== routeNamespace(to)) {
      listStore.save()
    }
  })
  watch(model, v => {
    listStore.save(v)
  })
}

onMounted(() => {
  if (!props.persist) {
    modelValueSet({})
    return
  }

  const stored = props.persistKey ? listStore.getHistoryItem(props.persistKey) : historyItem?.value
  if (stored) {
    modelValueSet(stored)
    searchTerm.value = stored.search
  } else {
    modelValueSet({})
  }

  watch(model, (...a) => {
    if (modalstack) {
      return
    }
    listStore.save(a[0], props.persistKey || undefined)
  })
})

if (props.persist && !modalstack) {
  onBeforeRouteLeave((to, from) => {
    if (routeNamespace(from) !== routeNamespace(to)) {
      listStore.save(model.value, props.persistKey || undefined)
    }
  })
}

function handleCreateReport(period) {
  emit('create_report', period)
}

function sortFn(column) {
  const m = { ...model.value }

  if (m.order_field !== column) {
    m.order_field = column
    m.order_direction = 'ASC'
  } else {
    switch (m.order_direction) {
      case 'ASC':
        m.order_direction = 'DESC'
        break
      case 'DESC':
        delete m.order_direction
        delete m.order_field
        break
      default:
        m.order_direction = 'ASC'
    }
  }
  model.value = m
}

function routeNamespace(rt) {
  const n = rt.name.split('.')
  n.pop()
  return n.join('.')
}

function modelValueSet(newob) {
  const v = { ...model.value, ...newob }

  if (v.filter) {
    const m = { ...model.value }

    if (newob.filter || newob.filter_id) {
      let currentFilters = m.filter ? String(m.filter).split('&&') : []
      let currentFilterIds = m.filter_id ? String(m.filter_id).split('&&') : []

      const filterIndex = currentFilters.indexOf(newob.filter)

      if (filterIndex !== -1) {
        if (newob.filter_id !== '') {
          currentFilterIds[filterIndex] = newob.filter_id
        } else {
          currentFilters.splice(filterIndex, 1)
          currentFilterIds.splice(filterIndex, 1)
        }
      } else {
        if (newob.filter_id !== '') {
          // Новые фильтры добавляем в начало (над старыми)
          currentFilters.unshift(newob.filter)
          currentFilterIds.unshift(newob.filter_id)
        }
      }

      v.filter = currentFilters.join('&&')
      v.filter_id = currentFilterIds.join('&&')
    }

    isfilter.value = true
  }

  for (const k of ['order_direction']) {
    if (!String(v[k]).trim().length) {
      delete v[k]
    }
  }

  for (const k of ['page', 'onPage']) {
    let val = v[k]

    if (!val) {
      continue
    }

    val = Number(val)

    if (isNaN(val)) {
      continue
    }

    v[k] = val
  }

  model.value = v
}

provide('sort-fn', sortFn)
provide('list-model', model)
provide('model-valueset', modelValueSet)
</script>
